import { Autocomplete, AutocompleteProps, TextField, Chip } from '@mui/material';

interface FilterOption {
	id: string | number;
	label: string;
	color?: string;
}

type CustomAutocompleteProps = Omit<AutocompleteProps<FilterOption, boolean, boolean, boolean>, 'renderInput'> & {
	label?: string;
	placeholder?: string;
	required?: boolean;
	error?: boolean;
	helperText?: string;
	/** Blocks typing in the input — the field only accepts a pick from the dropdown. */
	readOnly?: boolean;
};

export function CustomAutocomplete({
	label,
	placeholder,
	multiple,
	required,
	error,
	helperText,
	readOnly,
	sx,
	...props
}: CustomAutocompleteProps) {
	return (
		<Autocomplete
			{...props}
			multiple={multiple}
			// Keep the dropdown open while picking several options in multi-select mode
			disableCloseOnSelect={props.disableCloseOnSelect ?? multiple}
			isOptionEqualToValue={props.isOptionEqualToValue ?? ((option, value) => option.id === value.id)}
			size="small"
			slotProps={{
				...props.slotProps,
				paper: {
					sx: {
						'& .MuiAutocomplete-listbox': {
							'& .MuiAutocomplete-option': {
								color: 'var(--neutral-dark-base)',
								backgroundColor: 'transparent',
								'&:hover': {
									backgroundColor: 'var(--neutral-bg-light-colored) !important',
								},
								'&.Mui-focused': {
									backgroundColor: 'transparent !important',
									'&:hover': {
										backgroundColor: 'var(--neutral-bg-light-colored) !important',
									},
								},
								'&.Mui-selected': {
									backgroundColor: 'var(--neutral-bg-active) !important',
									'&:hover': {
										backgroundColor: 'var(--neutral-bg-light-colored) !important',
									},
								},
								'&:active': {
									backgroundColor: 'var(--neutral-bg-light-colored) !important',
								},
								'&[aria-selected="true"]': {
									backgroundColor: 'var(--neutral-bg-active) !important',
									'&:hover': {
										backgroundColor: 'var(--neutral-bg-light-colored) !important',
									},
								},
							},
						},
					},
				},
			}}
			sx={[
				{
					'& .MuiOutlinedInput-root': {
						borderRadius: '8px',
						cursor: 'pointer',
						// Fixed height for single-select; grow to fit wrapped chips for multi-select. The theme pins a fixed
						// height per input size, so the override needs !important or chips spill outside the border.
						...(multiple
							? { minHeight: '40px !important', height: 'auto !important', flexWrap: 'wrap', gap: '6px' }
							: { height: '40px' }),
						'& fieldset': {
							borderColor: 'var(--neutral-outline)',
							borderWidth: '1px',
						},
						'&:hover:not(.Mui-focused):not(.Mui-disabled) fieldset': {
							borderColor: 'var(--neutral-gray-medium)',
						},
						'&.Mui-focused fieldset': {
							borderColor: 'var(--primary-500)',
							borderWidth: '2px',
						},
						'&.Mui-disabled fieldset': {
							borderColor: 'var(--neutral-gray-disabled)',
						},
						'& input': {
							color: 'var(--neutral-dark-base)',
							cursor: 'pointer',
							fontSize: '16px',
						},
						'& .MuiAutocomplete-input': {
							color: 'var(--neutral-dark-base)',
							fontSize: '16px',
						},
						'&.Mui-disabled .MuiAutocomplete-input, &.Mui-disabled input': {
							color: 'var(--neutral-gray-disabled)',
							WebkitTextFillColor: 'var(--neutral-gray-disabled)',
						},
						'&.Mui-disabled': {
							cursor: 'not-allowed',
							pointerEvents: 'none',
						},
					},
					'& .MuiInputLabel-root': {
						color: 'var(--neutral-gray-medium)',
						'&.Mui-focused': {
							color: 'var(--primary-500)',
						},
						'&.Mui-disabled': {
							color: 'var(--neutral-gray-disabled)',
						},
					},
					'& .MuiSvgIcon-root': {
						color: 'var(--neutral-dark-base)',
					},
					'& .MuiAutocomplete-popupIndicator': {
						color: 'var(--neutral-dark-base)',
					},
					'& .MuiAutocomplete-popupIndicator.Mui-disabled, & .MuiAutocomplete-popupIndicator.Mui-disabled .MuiSvgIcon-root':
						{
							color: 'var(--neutral-gray-disabled)',
						},
				},
				...(Array.isArray(sx) ? sx : sx ? [sx] : []),
			]}
			renderTags={
				multiple
					? (selected, getTagProps) =>
							selected.map((option, index) => {
								const opt = option as FilterOption;
								return (
									<Chip
										{...getTagProps({ index })}
										key={opt.id}
										label={opt.label}
										size="small"
										// Options carrying their own colour (status chips) keep it;
										// plain options use the shared light chip look.
										sx={
											opt.color
												? {
														backgroundColor: opt.color,
														color: '#fff',
														'& .MuiChip-deleteIcon': {
															color: '#fff !important',
														},
													}
												: {
														backgroundColor: 'var(--neutral-bg-active)',
														color: 'var(--primary-500)',
														borderRadius: '4px',
														height: '28px',
														fontWeight: 400,
														fontSize: 14,
														maxWidth: '100%',
														'& .MuiChip-deleteIcon': {
															color: 'var(--primary-500) !important',
														},
													}
										}
									/>
								);
							})
					: undefined
			}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					placeholder={placeholder}
					required={required}
					error={error}
					helperText={helperText}
					slotProps={{ htmlInput: { ...params.inputProps, readOnly } }}
				/>
			)}
		/>
	);
}
